import * as React from 'react'
// libs
import styled from 'styled-components'
import { Helmet } from 'react-helmet-async'
// components
import Search from 'components/UI/PageSearch'
import {
  ChangeVisibilityButton,
  CreateSubPlanButton,
  PlanDetailsActionsMenu,
  PlanViewModesMenu,
  ShowArchivedPlanItemsButton,
  ShowPlanActivitiesButton
} from 'components/CTAs/index'
import { LockIcon } from 'components/UI'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
import { usePlanAccess, useShowModal } from 'hooks/index'
// view components
import PlanFilterDrawer from 'components/Modals/PlanFilterDrawer'
import CreateSubPlanModal from 'components/Modals/CreateSubPlanModal/index'
import LinkingSourceModal from 'components/Modals/LinkingSourceModal'
import MainLayoutContainer from 'containers/MainLayout/index'
import ObjectivesListContainer from 'containers/ObjectivesList'
import PlanActivitiesModal from 'components/Modals/PlanActivitiesModal'
import AuthorizationWarning from 'components/UI/Warnings/AuthorizationWarning'
import PlanDetailsContainer from 'containers/Plan/Details'
import PlanObjectivesListModal from 'components/Modals/MinimizedObjectivesListModal'
import PlanScheduleSettings from 'components/CTAs/PlanScheduleSettings'
import ToggleTableTreeButton from 'components/CTAs/ToggleTableTreeButton'
import PlanScheduleDefaultsModal from 'components/Modals/PlanScheduleDefaultsModal'
import PlanSettingsModal from 'components/Modals/PlanColumnsSettingsModal'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// constants
import { Modals } from 'constants/index'
// utils
import get from 'lodash/get'
import { ellipsis } from 'polished'

const PlanTitle = styled.div`
  ${ellipsis('100%')};
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: inherit;

  & > svg {
    margin-left: 0 !important;
  }
`

const PlanEdit: React.FC = () => {
  const { isLoading, isSubPlan, plan, setPlanVisibility, fieldFilters } = React.useContext(PlanEditPageContext)

  const { showModal: openPlanFilter } = useShowModal({ modalType: Modals.PLAN_FILTER_MODAL })

  const { planCreateSubPlan, planSetPlanSecurity } = get<any, any, any>(plan, 'currentPermissions', {})

  const { permissionsFromSecurityGroups } = useCurrentUser()

  const canArchivePlanItems =
    permissionsFromSecurityGroups?.objectiveArchiveObjective ||
    permissionsFromSecurityGroups?.actionArchiveAction ||
    permissionsFromSecurityGroups?.kpiArchiveKPI

  const shouldShowPlan = usePlanAccess(plan)

  return (
    <>
      <Helmet>
        <title>Skefto - Plan Edit</title>
      </Helmet>
      <MainLayoutContainer
        disableScroll
        isLoading={isLoading}
        label={
          <TitleContainer>
            {get(plan, 'locked') && <LockIcon />}
            <PlanTitle>{get(plan, 'name')}</PlanTitle>
          </TitleContainer>
        }
        CTA={
          shouldShowPlan ? (
            <>
              {planSetPlanSecurity && (
                <ChangeVisibilityButton entityType='plan' entity={plan} onSetVisibility={setPlanVisibility} />
              )}
              <Search onFilterOpen={openPlanFilter} isFilterActive={!!fieldFilters?.length} />
              <ToggleTableTreeButton />
              {isSubPlan && <PlanViewModesMenu />}
              <ShowPlanActivitiesButton />
              <PlanScheduleSettings />
              {canArchivePlanItems && <ShowArchivedPlanItemsButton />}
              {planCreateSubPlan && <CreateSubPlanButton />}
              {plan && <PlanDetailsActionsMenu />}
            </>
          ) : null
        }>
        {shouldShowPlan ? (
          <>
            <PlanDetailsContainer />
            <ObjectivesListContainer plan={plan} />

            <PlanFilterDrawer />
            <CreateSubPlanModal />
            <LinkingSourceModal />
            <PlanActivitiesModal />
            <PlanObjectivesListModal />
            <PlanScheduleDefaultsModal />
            <PlanSettingsModal />
          </>
        ) : (
          <AuthorizationWarning />
        )}
      </MainLayoutContainer>
    </>
  )
}

export default PlanEdit
